import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Button,
  Modal,
  Message,
  Icon,
  Label,
  Segment,
  Statistic,
} from "semantic-ui-react";
import { loanApi } from "@api/loans";
import { patronApi } from "@api/patrons";
import { userApi } from "@api/users/user";
import { invenioConfig } from "@config";
import { withCancel } from "@api/utils";
import { checkBorrowingLimit } from "@utils/CirculationUtils";
import ESSelector from "./ESSelector";
import debounce from "lodash.debounce";

// Constants from AvailableItems
const ROLES = {
  FACULTY_ONLY: ["faculty", "admin"],
  ALL_USER_TYPES: ["faculty", "admin", "researcher", "visitor", "student"],
  ADMIN: ["admin", "administrator", "super_admin"],
  STAFF: ["librarian", "admin", "staff", "administrator", "super_admin"],
};

const MESSAGES = {
  RESTRICTIONS: {
    OVERDUE: "Patron has overdue items. ",
    BORROWING_LIMIT: (current, limit) =>
      `Patron has reached borrowing limit (${current}/${limit}). `,
    CHECKOUT_BLOCKED: "Checkout blocked. ",
    OVERRIDE_SUFFIX:
      "The borrower is a faculty member, use the override option.",
    AUTO_CHECKOUT_BLOCKED: "Automatic checkout is blocked. ",
    AUTO_OVERRIDE_SUFFIX: "Contact a librarian to override restrictions.",
    FACULTY_AUTO_OVERRIDE_SUFFIX: "Use the override button to allow checkout.",
  },
  OVERRIDES: {
    ADMIN_ENABLED: "Admin override is enabled",
    FACULTY_ONLY: "Only admins can override restrictions.",
    NO_PERMISSION: "You do not have permission to override restrictions",
    LIBRARIAN_FACULTY:
      "Librarian can override restrictions for faculty members only",
    STUDENT: "Override is not available for students.",
    RESEARCHER: "Override is not available for researchers.",
    VISITOR: "Override is not available for visitors.",
  },
  LOADING: {
    PATRON_STATUS: "Checking patron status...",
    PATRON_ROLE: "Checking patron role...",
    BORROWING_LIMITS: "Checking borrowing limits...",
  },
};

export default class ESSelectorModal extends Component {
  constructor(props) {
    super(props);
    const { modalOpened } = this.props;
    this.state = {
      selections: [],
      visible: modalOpened,
      selectedPatronStatus: null,
      isCheckingPatronStatus: false,
      currentUser: null,
      overrideRestrictions: false,
    };
    this.patronCache = {};
    this.debouncedCheckPatronStatus = debounce(this.checkPatronStatus, 300);
    this._isMounted = false;
    this.cancellableRequests = [];
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchCurrentUser();
  }

  componentWillUnmount() {
    this._isMounted = false;

    // Cancel all pending requests
    this.cancellableRequests.forEach((request) => {
      if (request && request.cancel) {
        request.cancel();
      }
    });
    this.cancellableRequests = [];

    // Cancel debounced function
    this.debouncedCheckPatronStatus.cancel();
  }

  fetchCurrentUser = async () => {
    if (this.state.currentUser) return;
    try {
      const response = await userApi.get();
      if (this._isMounted) {
        this.setState({ currentUser: response?.data || response });
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  extractRoles = (entity) => {
    if (!entity?.roles || !Array.isArray(entity.roles)) {
      return [];
    }
    return entity.roles
      .map((role) => {
        if (typeof role === "object" && role.name) {
          return role.name.toLowerCase();
        }
        if (typeof role === "string") {
          return role.toLowerCase();
        }
        return "";
      })
      .filter(Boolean);
  };

  hasAnyRole = (userRoles, targetRoles) => {
    return userRoles.some((role) => targetRoles.includes(role));
  };

  isCurrentUserAdmin = () => {
    const { currentUser } = this.state;
    if (!currentUser) return false;
    const userRoles = this.extractRoles(currentUser);
    return this.hasAnyRole(userRoles, ROLES.ADMIN);
  };

  isCurrentUserLibrarian = () => {
    const { currentUser } = this.state;
    if (!currentUser) return false;
    const userRoles = this.extractRoles(currentUser);
    return userRoles.includes("librarian");
  };

  canPerformOverride = () => {
    const { currentUser } = this.state;
    if (!currentUser) return false;
    const userRoles = this.extractRoles(currentUser);
    return this.hasAnyRole(userRoles, ROLES.STAFF);
  };

  determinePatronUserType = async (patronPid) => {
    try {
      const patronResponse = await patronApi.get(patronPid);
      const patronData = patronResponse.data?.metadata;
      const profileId = patronData?.profile_id;
      let userType = null;

      // Handle profile_id prefixes
      if (profileId) {
        if (profileId.startsWith("FAC")) {
          userType = "faculty";
        } else if (profileId.startsWith("STU")) {
          userType = "student";
        } else if (profileId.startsWith("RES")) {
          userType = "researcher";
        } else if (profileId.startsWith("VIS")) {
          userType = "visitor";
        }
      }

      // Fallback to user_types if profile_id doesn't match
      if (
        !userType &&
        Array.isArray(patronData?.user_types) &&
        patronData.user_types.length > 0
      ) {
        const extractedType = patronData.user_types[0];
        const normalizedType =
          typeof extractedType === "string"
            ? extractedType.toLowerCase()
            : (
                extractedType.user_type ||
                extractedType.type ||
                extractedType.name ||
                extractedType
              ).toLowerCase();
        if (ROLES.ALL_USER_TYPES.includes(normalizedType)) {
          userType = normalizedType;
        }
      }

      return {
        userType,
        isFaculty: ROLES.FACULTY_ONLY.includes(userType),
      };
    } catch (error) {
      return { userType: null, isFaculty: false };
    }
  };

  canOverrideRestrictions = (patronInfo) => {
    const isLibrarian = this.isCurrentUserLibrarian();
    const canPerformOverride = this.canPerformOverride();
    // Allow override **only if patron is faculty** and user has staff privileges
    if (patronInfo?.isFaculty && (isLibrarian || canPerformOverride)) {
      return true;
    }

    return false; // No override for non-faculty patrons
  };

  checkPatronStatus = async (patronPid) => {
    if (!patronPid || !this._isMounted) return;

    if (this.patronCache[patronPid]) {
      if (this._isMounted) {
        this.setState({ selectedPatronStatus: this.patronCache[patronPid] });
      }
      return;
    }

    if (this._isMounted) {
      this.setState({ isCheckingPatronStatus: true });
    }

    try {
      const overdueQuery = loanApi
        .query()
        .overdue()
        .withPatronPid(patronPid)
        .withState(invenioConfig.CIRCULATION.loanActiveStates)
        .qs();

      const activeLoansQuery = loanApi
        .query()
        .withPatronPid(patronPid)
        .withState(invenioConfig.CIRCULATION.loanActiveStates)
        .qs();

      // Create cancellable requests
      const overdueRequest = withCancel(loanApi.list(overdueQuery));
      const activeLoansRequest = withCancel(loanApi.list(activeLoansQuery));

      // Store them for cleanup
      this.cancellableRequests.push(overdueRequest, activeLoansRequest);

      // Run all requests in parallel
      const [overdueResponse, borrowingStats, patronInfo, activeLoansResponse] =
        await Promise.all([
          overdueRequest.promise,
          checkBorrowingLimit(patronPid),
          this.determinePatronUserType(patronPid),
          activeLoansRequest.promise,
        ]);

      // Remove completed requests from tracking
      this.cancellableRequests = this.cancellableRequests.filter(
        (req) => req !== overdueRequest && req !== activeLoansRequest
      );

      if (!this._isMounted) return;

      const hasOverdueItems = overdueResponse.data.total > 0;
      const activeLoansCount = activeLoansResponse.data.total;
      const canCheckout = !hasOverdueItems && borrowingStats.canBorrow;

      const patronStatus = {
        hasOverdueItems,
        borrowingStats,
        patronInfo,
        activeLoansCount,
        canCheckout,
      };

      this.patronCache[patronPid] = patronStatus;

      if (this._isMounted) {
        this.setState({
          selectedPatronStatus: patronStatus,
          isCheckingPatronStatus: false,
        });
      }
    } catch (error) {
      if (this._isMounted && error !== "UNMOUNTED") {
        this.setState({ isCheckingPatronStatus: false });
      }
    }
  };

  onSelectionsUpdate = (selections) => {
    if (!this._isMounted) return;

    this.setState({ selections });

    if (selections.length > 0) {
      const selectedPatron = selections[0];
      const patronPid =
        selectedPatron.metadata.pid || selectedPatron.metadata.id?.toString();

      if (patronPid) {
        this.debouncedCheckPatronStatus(patronPid);
      }
    } else {
      this.setState({
        selectedPatronStatus: null,
        overrideRestrictions: false,
      });
    }
  };

  getRestrictionMessage = (patronStatus) => {
    if (!patronStatus) return "";

    let message = "";
    if (patronStatus.hasOverdueItems) {
      message += MESSAGES.RESTRICTIONS.OVERDUE;
    }
    if (!patronStatus.borrowingStats.canBorrow && patronStatus.borrowingStats) {
      message += MESSAGES.RESTRICTIONS.BORROWING_LIMIT(
        patronStatus.borrowingStats.current,
        patronStatus.borrowingStats.limit
      );
    }
    return message;
  };

  getOverrideMessage = (patronInfo) => {
    const isAdmin = this.isCurrentUserAdmin();
    const isLibrarian = this.isCurrentUserLibrarian();
    const canPerformOverride = this.canPerformOverride();

    if (isAdmin) return MESSAGES.OVERRIDES.ADMIN_ENABLED;
    if ((isLibrarian || canPerformOverride) && patronInfo?.isFaculty) {
      return MESSAGES.OVERRIDES.LIBRARIAN_FACULTY;
    }
    if (!canPerformOverride) return MESSAGES.OVERRIDES.NO_PERMISSION;

    switch (patronInfo?.userType) {
      case "faculty":
        return MESSAGES.OVERRIDES.LIBRARIAN_FACULTY;
      case "student":
        return MESSAGES.OVERRIDES.STUDENT;
      case "researcher":
        return MESSAGES.OVERRIDES.RESEARCHER;
      case "visitor":
        return MESSAGES.OVERRIDES.VISITOR;
      default:
        return MESSAGES.OVERRIDES.NO_PERMISSION;
    }
  };

  renderPatronStatusInfo = () => {
    const {
      selectedPatronStatus,
      isCheckingPatronStatus,
      overrideRestrictions,
      selections,
    } = this.state;

    if (!selectedPatronStatus && !isCheckingPatronStatus) return null;

    if (isCheckingPatronStatus) {
      return (
        <Segment
          textAlign="center"
          padded="very"
          style={{
            borderRadius: "12px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <Icon name="spinner" loading size="large" />
          <p style={{ marginTop: "0.5rem", fontWeight: 500 }}>
            Checking patron status...
          </p>
        </Segment>
      );
    }

    const {
      hasOverdueItems,
      borrowingStats,
      patronInfo,
      activeLoansCount,
      canCheckout,
    } = selectedPatronStatus;

    // Get selected patron for request status
    const selectedPatron = selections.length > 0 ? selections[0] : null;

    const hasRestrictions = hasOverdueItems || !borrowingStats.canBorrow;
    const canOverride = this.canOverrideRestrictions(patronInfo);
    const finalCanCheckout =
      canCheckout || (hasRestrictions && overrideRestrictions);

    return (
      <Segment
        style={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          padding: "1.5rem",
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1.5rem",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <strong style={{ fontSize: "1.1rem" }}>Patron Status:</strong>
            <Label
              color={finalCanCheckout ? "green" : "red"}
              size="medium"
              style={{ fontWeight: 600 }}
            >
              {finalCanCheckout ? "Can Checkout" : "Restricted"}
            </Label>
            {overrideRestrictions && (
              <Label color="orange" size="medium" style={{ fontWeight: 600 }}>
                Override Active
              </Label>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            gap: "2rem",
            marginBottom: "1rem",
            flexWrap: "wrap",
          }}
        >
          <Statistic color="blue" size="tiny">
            <Statistic.Label>Active Loans</Statistic.Label>
            <Statistic.Value>{activeLoansCount}</Statistic.Value>
          </Statistic>
          <Statistic color="teal" size="tiny">
            <Statistic.Label>Borrowing Limit</Statistic.Label>
            <Statistic.Value>
              {borrowingStats.current}/{borrowingStats.limit}
            </Statistic.Value>
          </Statistic>
          {/* Statistic for Role */}
          <Statistic color="purple" size="tiny">
            <Statistic.Label>Role</Statistic.Label>
            <Statistic.Value>
              {patronInfo.userType || "Unknown"}
            </Statistic.Value>
          </Statistic>
        </div>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            marginBottom: "1rem",
          }}
        >
          {hasOverdueItems && (
            <Label color="red" size="small">
              <Icon name="warning circle" />
              Overdue Items
            </Label>
          )}
          {!borrowingStats.canBorrow && (
            <Label color="orange" size="small">
              <Icon name="exclamation triangle" />
              Limit Reached
            </Label>
          )}

          {/* Request Status Tags */}
          {selectedPatron?.metadata.hasRequestForThisItem && (
            <Label color="green" size="small">
              <Icon name="check circle" />
              Request for This Copy
            </Label>
          )}
          {selectedPatron?.metadata.hasRequestForOtherCopy && (
            <Label color="blue" size="small">
              <Icon name="info circle" />
              Has Request
            </Label>
          )}
          {selectedPatron && !selectedPatron.metadata.hasRequest && (
            <Label color="grey" size="small">
              <Icon name="circle outline" />
              No Prior Request
            </Label>
          )}
        </div>
        {hasRestrictions && !overrideRestrictions && (
          <Message
            warning
            style={{
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              marginTop: "1rem",
            }}
          >
            <Message.Header>Checkout Restricted</Message.Header>
            <p>{this.getRestrictionMessage(selectedPatronStatus)}</p>
            {canOverride ? (
              <Button
                size="small"
                color="orange"
                icon="unlock"
                labelPosition="left"
                content="Override Restrictions"
                onClick={() => this.setState({ overrideRestrictions: true })}
                style={{ marginTop: "0.5rem" }}
              />
            ) : (
              <Message info size="small" style={{ marginTop: "0.5rem" }}>
                <Icon name="lock" />
                {this.getOverrideMessage(patronInfo)}
              </Message>
            )}
          </Message>
        )}
        {hasRestrictions && overrideRestrictions && (
          <Message
            positive
            style={{
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              marginTop: "1rem",
            }}
          >
            <Message.Header>Restrictions Overridden</Message.Header>
            <p>{this.getRestrictionMessage(selectedPatronStatus)}</p>
            {canOverride && (
              <Button
                size="small"
                icon="lock"
                labelPosition="left"
                content="Restore Restrictions"
                onClick={() => this.setState({ overrideRestrictions: false })}
                style={{ marginTop: "0.5rem" }}
              />
            )}
          </Message>
        )}
      </Segment>
    );
  };

  canSave = () => {
    const { selections, selectedPatronStatus, overrideRestrictions } =
      this.state;

    if (!selections || selections.length === 0) return false;

    if (!selectedPatronStatus) return true; // Allow save if status hasn't been checked yet

    const { canCheckout, hasOverdueItems, borrowingStats } =
      selectedPatronStatus;
    const hasRestrictions = hasOverdueItems || !borrowingStats.canBorrow;

    return canCheckout || (hasRestrictions && overrideRestrictions);
  };

  toggle = () => {
    if (!this._isMounted) return;

    const { visible } = this.state;
    this.setState({
      visible: !visible,
      // Reset state when closing modal
      selectedPatronStatus: null,
      overrideRestrictions: false,
    });
  };

  save = () => {
    const { onSave } = this.props;
    const { selections, selectedPatronStatus, overrideRestrictions } =
      this.state;

    if (onSave) {
      // Pass additional patron status information to onSave
      const result = onSave(selections, {
        patronStatus: selectedPatronStatus,
        overrideActive: overrideRestrictions,
      });

      // Only close modal if onSave returns true or undefined (not false)
      if (result !== false) {
        this.toggle();
      }
    } else {
      this.toggle();
    }
  };

  render() {
    const {
      title,
      content,
      selectorComponent,
      size,
      saveButtonContent,
      trigger,
    } = this.props;

    const { visible } = this.state;
    const modalTrigger = React.cloneElement(trigger, {
      onClick: this.toggle,
    });
    const Selector = selectorComponent;

    return (
      <Modal
        id="es-selector-modal"
        open={visible}
        trigger={modalTrigger}
        size={size}
        centered={false}
        onClose={this.toggle}
      >
        <Modal.Header>{title}</Modal.Header>
        <Modal.Content>
          <div>{content}</div>
          <Selector
            onSelectionsUpdate={this.onSelectionsUpdate}
            {...this.props}
          />
          {this.renderPatronStatusInfo()}
        </Modal.Content>
        <Modal.Actions>
          <Button color="black" onClick={this.toggle}>
            Close
          </Button>
          <Button
            positive
            disabled={!this.canSave()}
            icon="checkmark"
            labelPosition="left"
            content={saveButtonContent}
            onClick={this.save}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

ESSelectorModal.propTypes = {
  trigger: PropTypes.node.isRequired,
  title: PropTypes.string,
  size: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isSelectionRequired: PropTypes.bool,
  initialSelections: PropTypes.array,
  onSelectResult: PropTypes.func,
  onSave: PropTypes.func,
  modalOpened: PropTypes.bool,
  selectorComponent: PropTypes.elementType,
  saveButtonContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

ESSelectorModal.defaultProps = {
  size: "small",
  saveButtonContent: "Save",
  title: "",
  selectorComponent: ESSelector,
  modalOpened: false,
  content: null,
  isSelectionRequired: true,
  initialSelections: [],
  onSelectResult: null,
  onSave: null,
};
